import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUserPrizes, UserPrize } from '@/hooks/useUserPrizes';
import { 
  Gift, 
  Trophy, 
  Star, 
  Clock, 
  CheckCircle,
  Sparkles,
  QrCode
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const getRarityStyles = (rarity: string) => {
  switch (rarity) {
    case 'legendary':
      return {
        bg: 'bg-gradient-to-r from-amber-100 to-yellow-50',
        border: 'border-amber-400',
        badge: 'bg-gradient-to-r from-amber-500 to-yellow-400 text-white',
        text: 'text-amber-700',
        icon: <Sparkles className="h-5 w-5 text-amber-500" />,
      };
    case 'epic':
      return {
        bg: 'bg-gradient-to-r from-purple-100 to-violet-50',
        border: 'border-purple-400',
        badge: 'bg-gradient-to-r from-purple-500 to-violet-400 text-white',
        text: 'text-purple-700',
        icon: <Trophy className="h-5 w-5 text-purple-500" />,
      };
    case 'rare':
      return {
        bg: 'bg-gradient-to-r from-blue-100 to-cyan-50',
        border: 'border-blue-400',
        badge: 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white',
        text: 'text-blue-700',
        icon: <Star className="h-5 w-5 text-blue-500" />,
      };
    default:
      return {
        bg: 'bg-gradient-to-r from-gray-100 to-slate-50',
        border: 'border-gray-300',
        badge: 'bg-gray-500 text-white',
        text: 'text-gray-700',
        icon: <Gift className="h-5 w-5 text-gray-500" />,
      };
  }
};

const getRarityLabel = (rarity: string) => {
  switch (rarity) {
    case 'legendary': return 'Legendario';
    case 'epic': return 'Épico';
    case 'rare': return 'Raro';
    default: return 'Común';
  }
};

const PrizeCard: React.FC<{ prize: UserPrize; onClick: () => void }> = ({ prize, onClick }) => {
  const rarity = prize.reward?.rarity || 'common';
  const styles = getRarityStyles(rarity);
  const isActive = prize.status === 'active';

  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md ${styles.bg} ${styles.border} border-2 ${
        !isActive ? 'opacity-60' : ''
      }`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-white/80">
            {styles.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className={`font-semibold ${styles.text} truncate`}>
                {prize.reward?.name || 'Premio'}
              </h4>
              <Badge className={`${styles.badge} text-xs`}>
                {getRarityLabel(rarity)}
              </Badge>
            </div>
            {prize.reward?.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {prize.reward.description}
              </p>
            )}
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-xs">
                Nivel {prize.level_earned}
              </Badge>
              {isActive ? (
                <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Disponible
                </span>
              ) : (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Canjeado
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PrizeQRModal: React.FC<{ 
  prize: UserPrize | null; 
  open: boolean; 
  onClose: () => void 
}> = ({ prize, open, onClose }) => {
  if (!prize) return null;

  const styles = getRarityStyles(prize.reward?.rarity || 'common');
  const qrData = JSON.stringify({
    type: 'prize',
    token: prize.prize_qr_token,
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {styles.icon}
            {prize.reward?.name || 'Premio'}
          </DialogTitle>
          <DialogDescription>
            {prize.status === 'active' 
              ? 'Muestra este QR al personal para canjear tu premio'
              : 'Este premio ya ha sido canjeado'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center py-4">
          {prize.status === 'active' ? (
            <>
              <div className={`p-4 rounded-2xl ${styles.bg} border-2 ${styles.border}`}>
                <QRCodeSVG
                  value={qrData}
                  size={180}
                  level="H"
                  includeMargin={false}
                />
              </div>
              <Badge className={`mt-4 ${styles.badge}`}>
                {getRarityLabel(prize.reward?.rarity || 'common')}
              </Badge>
              <p className="text-sm text-muted-foreground mt-2 text-center">
                {prize.reward?.description}
              </p>
              <p className="text-xs text-muted-foreground mt-4">
                Ganado al alcanzar nivel {prize.level_earned}
              </p>
            </>
          ) : (
            <div className="text-center py-6">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <p className="font-semibold text-green-700">Premio canjeado</p>
              <p className="text-sm text-muted-foreground mt-1">
                {prize.redeemed_at && format(new Date(prize.redeemed_at), "d 'de' MMMM, yyyy", { locale: es })}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const UserPrizesSection: React.FC = () => {
  const { activePrizes, redeemedPrizes, loading } = useUserPrizes();
  const [selectedPrize, setSelectedPrize] = useState<UserPrize | null>(null);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-24 bg-muted rounded-lg" />
            <div className="h-24 bg-muted rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasPrizes = activePrizes.length > 0 || redeemedPrizes.length > 0;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-secondary" />
            Mis Premios de Nivel
          </CardTitle>
          <CardDescription>
            Premios desbloqueados al subir de nivel
          </CardDescription>
        </CardHeader>

        <CardContent>
          {!hasPrizes ? (
            <div className="text-center py-8">
              <Trophy className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-muted-foreground">
                Aún no tienes premios. ¡Sube de nivel para desbloquearlos!
              </p>
            </div>
          ) : (
            <Tabs defaultValue="active">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="active" className="flex items-center gap-2">
                  <QrCode className="h-4 w-4" />
                  Disponibles ({activePrizes.length})
                </TabsTrigger>
                <TabsTrigger value="redeemed" className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Canjeados ({redeemedPrizes.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="active" className="space-y-3">
                {activePrizes.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    No tienes premios pendientes de canjear
                  </p>
                ) : (
                  activePrizes.map((prize) => (
                    <PrizeCard 
                      key={prize.id} 
                      prize={prize} 
                      onClick={() => setSelectedPrize(prize)} 
                    />
                  ))
                )}
              </TabsContent>

              <TabsContent value="redeemed" className="space-y-3">
                {redeemedPrizes.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    No has canjeado ningún premio todavía
                  </p>
                ) : (
                  redeemedPrizes.map((prize) => (
                    <PrizeCard 
                      key={prize.id} 
                      prize={prize} 
                      onClick={() => setSelectedPrize(prize)} 
                    />
                  ))
                )}
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>

      <PrizeQRModal
        prize={selectedPrize}
        open={!!selectedPrize}
        onClose={() => setSelectedPrize(null)}
      />
    </>
  );
};

export default UserPrizesSection;
